import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { API, ASSETS } from "@/lib/gp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

async function post(path, body) {
  const response = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Something went wrong. Please try again.");
  return data;
}

export default function PasswordReset() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (event) => {
    event.preventDefault();
    if (token && password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      if (token) {
        await post("/auth/reset-password", { token, password });
        toast.success("Password reset. Please sign in.");
        navigate("/login", { replace: true });
      } else {
        await post("/auth/forgot-password", { email });
        setSent(true);
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-5 py-16 md:py-24">
      <div className="glass rounded-3xl p-8">
        <div className="flex flex-col items-center text-center">
          <img src={ASSETS.avatar} alt="Yorick" className="h-16 w-16 rounded-2xl object-cover ring-1 ring-gp-blue/40" />
          <h1 className="mt-4 font-unbounded font-black tracking-tighter text-3xl uppercase">
            {token ? "Choose a new password" : "Reset your password"}
          </h1>
          <p className="text-white/60 mt-2 text-sm">
            {token ? "Enter a new password with at least eight characters." : "We'll email a secure, one-use reset link to your account."}
          </p>
        </div>

        {sent ? (
          <div className="mt-7 text-center">
            <p className="text-white/70">If an account exists for that email, the reset link is on its way. Check your inbox and spam folder.</p>
            <Link to="/login" className="inline-block mt-5 text-gp-blue font-semibold hover:underline">Return to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-7 space-y-3" data-testid="password-reset-form">
            {token ? (
              <>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="New password" minLength={8} maxLength={128} required className="pl-10 bg-black/30 border-white/10 h-12 rounded-xl" />
                </div>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input type="password" value={confirm} onChange={(event) => setConfirm(event.target.value)} placeholder="Confirm new password" minLength={8} maxLength={128} required className="pl-10 bg-black/30 border-white/10 h-12 rounded-xl" />
                </div>
              </>
            ) : (
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
                <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="Email" required className="pl-10 bg-black/30 border-white/10 h-12 rounded-xl" />
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full rounded-full py-6 bg-gp-blue hover:bg-gp-blue/90 text-white font-bold">
              {busy ? <Loader2 className="animate-spin" size={18} /> : token ? "Set new password" : "Send reset link"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
